/**
 * Authentication context wrapping Teams SSO and MSAL.
 *
 * Provides convenient access to auth state and actions
 * throughout the application. Automatically uses Teams SSO
 * when running in Teams, falls back to MSAL in browser.
 *
 * Also syncs the user to the database on authentication.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useMutation, gql } from "@apollo/client";
import { useTeamsAuth } from "@/components/providers/TeamsAuthProvider";
import type { UserAccessLevel } from "@/types";

// GraphQL Mutation inline
const SYNC_CURRENT_USER = gql`
  mutation SyncCurrentUser($input: SyncCurrentUserInput!) {
    syncCurrentUser(input: $input) {
      success
      message
      user {
        id
        email
        firstName
        lastName
        isActive
        accessLevel
        createdAt
        lastLoginAt
      }
    }
  }
`;

/**
 * User information from Azure AD.
 */
interface User {
  id: string;
  email: string;
  name: string;
}

/**
 * Database user information (from our backend).
 */
interface DbUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  accessLevel: UserAccessLevel;
  createdAt: string;
  lastLoginAt: string | null;
}

/**
 * Auth context value type.
 */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isTeams: boolean;
  user: User | null;
  dbUser: DbUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth provider component.
 *
 * Wraps TeamsAuth functionality in a simpler API.
 * Uses Teams SSO when in Teams, MSAL in browser.
 * Syncs user to database on successful authentication.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const teamsAuth = useTeamsAuth();
  const [dbUser, setDbUser] = useState<DbUser | null>(null);
  const [hasSynced, setHasSynced] = useState(false);

  const [syncUser] = useMutation(SYNC_CURRENT_USER);

  /**
   * User object derived from TeamsAuth.
   */
  const user = useMemo<User | null>(() => {
    if (!teamsAuth.user) return null;

    return {
      id: teamsAuth.user.id || "",
      email: teamsAuth.user.email || "",
      name: teamsAuth.user.name || teamsAuth.user.email || "Unknown",
    };
  }, [teamsAuth.user]);

  /**
   * Sync user to database when authenticated.
   */
  const syncUserToDb = useCallback(async () => {
    if (!teamsAuth.isAuthenticated || !teamsAuth.user?.id || hasSynced) {
      return;
    }

    try {
      // Get tenant ID from token
      const token = await teamsAuth.getAccessToken();
      let tenantId = "";

      if (token) {
        try {
          const parts = token.split(".");
          if (parts.length === 3) {
            const payload = parts[1];
            const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split("")
                .map(
                  (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                )
                .join(""),
            );
            const claims = JSON.parse(jsonPayload);
            tenantId = claims.tid || "";
          }
        } catch (e) {
          console.warn(
            "[AuthContext] Failed to decode tenant ID from token:",
            e,
          );
        }
      }

      // Parse name into first/last name
      const nameParts = (teamsAuth.user.name || "").split(" ");
      const firstName = nameParts[0] || null;
      const lastName =
        nameParts.length > 1 ? nameParts.slice(1).join(" ") : null;

      const result = await syncUser({
        variables: {
          input: {
            azureObjectId: teamsAuth.user.id,
            azureTenantId: tenantId,
            email: teamsAuth.user.email || "",
            firstName,
            lastName,
          },
        },
      });

      if (result.data?.syncCurrentUser?.success) {
        setDbUser(result.data.syncCurrentUser.user);
        console.log(
          "[AuthContext] User synced to database:",
          result.data.syncCurrentUser.user,
        );
      } else {
        console.error(
          "[AuthContext] Failed to sync user:",
          result.data?.syncCurrentUser?.message,
        );
      }
    } catch (error) {
      console.error("[AuthContext] Error syncing user to database:", error);
    } finally {
      setHasSynced(true);
    }
  }, [
    teamsAuth.isAuthenticated,
    teamsAuth.user,
    teamsAuth.getAccessToken,
    syncUser,
    hasSynced,
  ]);

  /**
   * Sync user on authentication.
   */
  useEffect(() => {
    if (teamsAuth.isAuthenticated && !teamsAuth.isLoading && !hasSynced) {
      syncUserToDb();
    }
  }, [teamsAuth.isAuthenticated, teamsAuth.isLoading, hasSynced, syncUserToDb]);

  /**
   * Reset sync state on logout.
   */
  useEffect(() => {
    if (!teamsAuth.isAuthenticated) {
      setHasSynced(false);
      setDbUser(null);
    }
  }, [teamsAuth.isAuthenticated]);

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: teamsAuth.isAuthenticated,
      isLoading: teamsAuth.isLoading,
      isTeams: teamsAuth.isTeams,
      user,
      dbUser,
      login: teamsAuth.login,
      logout: teamsAuth.logout,
    }),
    [
      teamsAuth.isAuthenticated,
      teamsAuth.isLoading,
      teamsAuth.isTeams,
      user,
      dbUser,
      teamsAuth.login,
      teamsAuth.logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export default AuthContext;
